// ─────────────────────────────────────────────────────────────
// API service layer — Laravel REST backend via VITE_API_BASE_URL
// Cache-first strategy using localStorage (8-hour TTL)
// ─────────────────────────────────────────────────────────────

import { clearAuth, getToken } from "./auth-storage.ts"
import type { PermissionLevel, PermissionMap } from "./auth-storage.ts"

// Τρεις καταστάσεις, σκόπιμα διακριτές:
//   undefined  → η μεταβλητή δεν ορίστηκε καθόλου· σφάλμα με σαφές μήνυμα.
//   ""         → same-origin. Οι κλήσεις πάνε στο /api/... του ίδιου host,
//                όπου τις πιάνει ο dev proxy του Vite (δες vite.config.ts).
//   "http://…" → απόλυτο URL προς άλλον host.
const RAW_BASE = import.meta.env.VITE_API_BASE_URL as string | undefined
const IS_CONFIGURED = RAW_BASE !== undefined
const BASE_URL = (RAW_BASE ?? "").replace(/\/$/, "")

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE"

const REQUEST_TIMEOUT_MS = 15_000

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /**
     * Μηνύματα ανά πεδίο, μόνο από validation 422. Κενό παντού αλλού — το ωμό
     * σώμα του backend δεν φτάνει ποτέ στο UI, τα δικά μας μηνύματα ναι.
     */
    public readonly errors: Record<string, string[]> = {},
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/** Μήνυμα για τον χρήστη. Το ωμό σώμα του backend ΔΕΝ φτάνει ποτέ στο UI. */
function userMessage(status: number): string {
  if (status === 0) return "Αδυναμία σύνδεσης με τον διακομιστή."
  if (status === 401 || status === 403) return "Δεν έχετε πρόσβαση σε αυτή την ενέργεια."
  if (status === 404) return "Δεν βρέθηκε."
  if (status === 409) return "Η εγγραφή υπάρχει ήδη."
  if (status === 422) return "Μη έγκυρα δεδομένα. Ελέγξτε τα πεδία."
  if (status >= 500) return "Σφάλμα διακομιστή. Δοκιμάστε ξανά."
  return "Κάτι πήγε στραβά. Δοκιμάστε ξανά."
}

export async function apiFetch<T>(
  path: string,
  method: HttpMethod = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  if (!IS_CONFIGURED) {
    throw new ApiError(0, "Το VITE_API_BASE_URL δεν έχει οριστεί — δεν έχει συνδεθεί backend.")
  }

  // Χωρίς timeout ένα κρεμασμένο socket αφήνει το UI σε skeleton για πάντα.
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS)

  let res: Response
  try {
    res = await fetch(`${BASE_URL}/api${path}`, {
      method,
      headers: {
        // Χωρίς Accept, η Laravel κάνει content-negotiate σε HTML error page
        // και το res.json() σκάει με SyntaxError αντί για καθαρό σφάλμα.
        Accept: "application/json",
        // Μόνο όταν υπάρχει σώμα — αλλιώς κάθε GET προκαλεί περιττό CORS preflight.
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        // Bearer token — χωρίς αυτό κάθε προστατευμένο endpoint απαντά 401.
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: signal ? AbortSignal.any([signal, timeout]) : timeout,
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e
    throw new ApiError(0, userMessage(0))
  }

  if (!res.ok) {
    // 401 = το token έληξε/ανακλήθηκε (ή δεν υπήρξε ποτέ). Καθάρισε το session
    // και πήγαινε στο login — εκτός αν είμαστε ΗΔΗ εκεί (αποφυγή loop: το ίδιο
    // το login απαντά 422 στα λάθος στοιχεία, οπότε 401 στο /login σημαίνει
    // stale token από προηγούμενη συνεδρία).
    if (res.status === 401 && !window.location.pathname.startsWith("/login")) {
      clearAuth()
      window.location.assign("/login")
    }
    // Οι λεπτομέρειες πάνε στην κονσόλα για debugging, όχι στην οθόνη.
    const detail = await res.text().catch(() => "")
    console.error(`API ${method} ${path} → ${res.status}`, detail)
    if (res.status === 422) {
      try {
        const body = JSON.parse(detail) as { message?: string; errors?: Record<string, string[]> }
        const first = body.errors ? Object.values(body.errors)[0]?.[0] : undefined
        throw new ApiError(422, first ?? body.message ?? userMessage(422), body.errors ?? {})
      } catch (e) {
        // Μη-JSON σώμα: πέφτουμε στο γενικό μήνυμα παρακάτω.
        if (e instanceof ApiError) throw e
      }
    }
    throw new ApiError(res.status, userMessage(res.status))
  }

  // 204 ή κενό σώμα: το res.json() θα πετούσε SyntaxError σε ΕΠΙΤΥΧΗΜΕΝΟ mutation.
  if (res.status === 204 || res.headers.get("content-length") === "0") return undefined as T
  const contentType = res.headers.get("content-type") ?? ""
  if (!contentType.includes("json")) return undefined as T
  return (await res.json()) as T
}

// ── Cache helpers ────────────────────────────────────────────
//
// Ήταν cookies. Ένα cookie έχει όριο ~4 KB, οπότε το erp_sasi χωρούσε ~27
// εγγραφές· πάνω από εκεί ο browser απέρριπτε το write ΣΙΩΠΗΛΑ (δεν πετάει
// exception — το try/catch δεν έπιανε τίποτα). Χειρότερα, ένα cookie που είχε
// γραφτεί όσο η λίστα ήταν μικρή επιβίωνε 7 ημέρες, σερβίροντας snapshot με
// λείπουσες νέες γραμμές. Και τα ~19 keys ανέβαιναν σε ΚΑΘΕ same-origin
// request, assets συμπεριλαμβανομένων.
//
// Το localStorage δίνει ~5 MB, δεν ταξιδεύει με τα requests, και πετάει
// κανονικά σε υπέρβαση quota.

const CACHE_TTL_MS = 8 * 60 * 60 * 1000 // μία βάρδια

type CacheEnvelope<T> = { t: number; data: T }

export function getCookie<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const env = JSON.parse(raw) as CacheEnvelope<T>
    if (!env || typeof env.t !== "number") return null
    if (Date.now() - env.t > CACHE_TTL_MS) {
      localStorage.removeItem(key)
      return null
    }
    return env.data
  } catch {
    return null
  }
}

export function setCookie<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ t: Date.now(), data: value } satisfies CacheEnvelope<T>))
  } catch {
    // Υπέρβαση quota ή private mode — το cache είναι προαιρετικό, συνεχίζουμε.
  }
}

export function deleteCookie(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

/**
 * Σβήνει τα cookies που έγραφε η προηγούμενη υλοποίηση.
 *
 * Χωρίς αυτό, όποιος έχει ήδη χρησιμοποιήσει την εφαρμογή κουβαλά έως 19
 * `erp_*` cookies σε κάθε request για 7 ακόμα ημέρες. Καλείται μία φορά
 * στην εκκίνηση.
 */
export function purgeLegacyCookies(): void {
  try {
    for (const entry of document.cookie.split("; ")) {
      const name = entry.split("=")[0]
      if (name?.startsWith("erp_")) {
        document.cookie = `${name}=; max-age=0; path=/`
      }
    }
  } catch {
    // ignore
  }
}

// ── Types ────────────────────────────────────────────────────

export type SasiRecord = {
  sku: string
  width: number
  height: number
  direction: "left" | "right"
  stock: number
  reserved: number
}

/** Εσωτερικό ή εξωτερικό — μόνο το περβάζι υπάρχει και στις δύο, η κάσα είναι ένα σκέτο εξωτερικό στοιχείο. */
export type PervaziSide = "internal" | "external"

// Η κάσα ΔΕΝ έχει πλευρά: είναι αποκλειστικά εξωτερικό στοιχείο, μία ανά πόρτα.
export type KasaRecord = { sku: string; color: string; size: string; stock: number }
// Το `side` αφορά ΜΟΝΟ το περβάζι — είναι το μοναδικό ξύλινο εξάρτημα που
// υπάρχει και εσωτερικά και εξωτερικά. Προαιρετικό στον τύπο επειδή η
// επένδυση μοιράζεται το ίδιο σχήμα και τον ίδιο κοινό πίνακα στο UI.
export type PervaziRecord = { sku: string; color: string; size: string; stock: number; side?: PervaziSide }
export type EpendysiRecord = { sku: string; color: string; size: string; stock: number }

export type SettingRecord = {
  id: number | string
  name: string
  status: "active" | "inactive"
  /**
   * Πόσες κάσες παίρνει το μέγεθος. Υπάρχει ΜΟΝΟ στα «Μεγέθη Κάσας (Θωρ.)»
   * — null παντού αλλού, γιατί ένα χρώμα ή ένας προμηθευτής δεν έχει ποσότητα.
   */
  quantity?: number | null
  /** Μόνο στα «Χρώματα Εσωτερικών»: κάθε προμηθευτής έχει δικό του χρωματολόγιο. */
  supplier_id?: number | null
  supplier_name?: string
  created_at?: string
}

export type CustomerRecord = {
  id: number
  first_name: string
  /** NULL μόνο σε καρτέλα που έφτιαξε το backfill από παλιά παραγγελία. */
  last_name: string | null
  phone: string | null
  address: string | null
  afm: string | null
  notes: string | null
  /** «Επώνυμο Όνομα» — η σειρά που γράφεται και στο παραστατικό. */
  full_name: string
  /** Λείπει επώνυμο, διεύθυνση ή ΑΦΜ. Υπολογίζεται από τον server. */
  is_incomplete: boolean
  orders_count: number
  last_order_at: string | null
  created_at: string
}

export type CustomerPayload = {
  first_name: string
  last_name: string
  phone: string
  address: string
  afm: string
  notes: string
}

/** Μία πόρτα αποθηκευμένης παραγγελίας. */
export type OrderDoorRecord = {
  id: number
  position: number
  sasi_sku: string | null
  /** Εσωτερικές: το φάρδος που ζήτησε ο πελάτης (1–93). Θωρακισμένες: το φάρδος του σασί. */
  width: number | null
  /**
   * Εσωτερικές: η πόρτα αποθέματος που κόβεται (63…93). null = παλιά γραμμή που
   * δεσμεύει με το `width` της. Δεν μπαίνει ποτέ από τον πωλητή σε θωρακισμένη.
   */
  stock_width: number | null
  height: number | null
  direction: "left" | "right" | null
  frame_size: string
  accessories_finish: string
  exterior_cladding: "none" | "standard" | "aluminium"
  exterior_color: string
  exterior_design: string
  exterior_aluminium_order_id: number | null
  exterior_aluminium_code?: string
  exterior_aluminium_description?: string
  interior_cladding: "none" | "standard" | "aluminium"
  interior_color: string
  interior_design: string
  interior_aluminium_order_id: number | null
  interior_aluminium_code?: string
  interior_aluminium_description?: string
  notes: string
  // ── Εσωτερικές — null/κενά στις θωρακισμένες ──
  interior_door_id: number | null
  door_kind: InteriorDoorType | null
  is_sliding: boolean
  kasa_type: string | null
  model: string
  color: string
  supplier_name: string
  /** Παλιός διακόπτης ναι/όχι — διαβάζεται μόνο για παλιές παραγγελίες. */
  needs_ksilogonia: boolean
  /** Πόσες ξυλογωνίες θέλει η πόρτα. 0 = καμία. */
  ksilogonia_quantity: number
  /** Πόσα περβάζια τραβά η πόρτα. 0 = κανένα. Προεπιλογή 2,5 στις εσωτερικές. */
  pervazi_quantity: number
  /** Κάσες ανά πόρτα — ισχύει ΜΟΝΟ στη συρόμενη (προεπιλογή 2,5)· η κανονική τραβά πάντα 2,5. */
  kasa_quantity: number
  /** Μπινιάστρες ανά πόρτα — μόνο στη συρόμενη (προεπιλογή 0). */
  binia_quantity: number
  /**
   * Πότε έφυγε ΑΥΤΗ η πόρτα — ανά πόρτα, όχι ανά παραγγελία: σε μεγάλη
   * παραγγελία ο πελάτης μπορεί να πάρει μία έτοιμη πόρτα πριν τις υπόλοιπες.
   * null/undefined = δεν έχει φύγει ακόμα. Μόνο το /deliver το θέτει.
   */
  delivered_at?: string | null
  /** Ποιος σημείωσε την παράδοση. Κενό αν δεν έχει παραδοθεί ή αν ο χρήστης διαγράφηκε έκτοτε. */
  delivered_by_name?: string
}

/**
 * Η όψη παραγωγής μιας παραγγελίας (/production/:id) — ΧΩΡΙΣ οικονομικά.
 *
 * Ξεχωριστός τύπος από το OrderRecord αντί για ένα υποσύνολο των πεδίων του:
 * ο server (OrderController::productionShow) ΔΕΝ στέλνει καθόλου total,
 * deposit, balance, vat_amount, paid_at σε αυτή την απάντηση — δεν είναι
 * απλώς πεδία που η οθόνη αγνοεί.
 */
export type ProductionOrderRecord = {
  id: number
  order_number: string
  door_type: "security" | "interior"
  customer_name: string
  status: "ready_for_production" | "in_production" | "ready"
  notes: string
  doors: OrderDoorRecord[]
}

/** Γιατί εμφανίστηκε μια γραμμή στην αναζήτηση. `null` = ταίριαξε η κεφαλίδα. */
export type OrderMatch = { door: number; field: string; value: string }

/** «Πόρτα 2 · χρώμα: Λευκό Γυαλιστερό» — κενό όταν δεν υπάρχει ταίριασμα πόρτας. */
export function matchSummary(m?: OrderMatch | null): string {
  return m ? `Πόρτα ${m.door} · ${m.field}: ${m.value}` : ""
}

export type OrderRecord = {
  id: number
  order_number: string
  door_type?: "security" | "interior"
  customer_name: string
  customer_phone: string
  /** NULL μόνο σε παραγγελία παλαιότερη από τη βάση πελατών. */
  customer_id: number | null
  /**
   * Η ζωντανή καρτέλα, όπως τη δίνει ο server από το customer_id. Έρχεται μόνο
   * στις απαντήσεις που περιέχουν και τις πόρτες (/orders/get/{id}, create,
   * update) — στη λίστα λείπει, γι' αυτό είναι προαιρετική. `null` σημαίνει
   * παραγγελία χωρίς συνδεδεμένη καρτέλα, όχι «δεν βρέθηκε».
   */
  customer?: CustomerRecord | null
  /**
   * Η οικοδομή, ως απλό ελεύθερο κείμενο. Αντικατέστησε τον παλιό picker
   * (site_id/sites) στη ροή παραγγελίας — ο ιδιοκτήτης έκρινε την
   * καταχωρημένη οντότητα υπερβολή, και η οντότητα αφαιρέθηκε εντελώς.
   */
  building?: string
  /** Ο πωλητής που την έγραψε. Κενό στις παλιές — όχι σφάλμα. */
  created_by_name?: string
  /** Ο κωδικός πριν τη μετατροπή μορφής, για αντιστοίχιση παλιάς εκτύπωσης. */
  legacy_code?: string | null
  matched?: OrderMatch | null
  door_count: number
  status: "pending" | "ready_for_production" | "in_production" | "ready" | "delivered" | "cancelled"
  delivery_type: "installation" | "pickup" | "shipping"
  delivery_address?: string
  /** ISO "YYYY-MM-DD", ή null όταν δεν έχει οριστεί. ΠΡΟΑΙΡΕΤΙΚΟ — συχνά άγνωστη τη στιγμή της καταχώρησης. */
  delivery_date?: string | null
  notes?: string
  doors?: OrderDoorRecord[]
  total?: number
  deposit?: number
  /** null = δεν έχει επιλεγεί ακόμα — δεν εφευρίσκουμε προεπιλογή. */
  payment_method?: "cash" | "bank" | "card" | null
  balance?: number
  /** Σήμα για τη λίστα: μπήκε προκαταβολή, ανεξάρτητα από το ποσό. */
  deposit_paid?: boolean
  /** ΦΠΑ σε ευρώ — το γράφει ο ιδιοκτήτης με το χέρι, όχι ποσοστό. */
  vat_amount?: number
  /** Πότε εξοφλήθηκε. null/undefined = όχι ακόμα — μόνο το /mark-paid το θέτει. */
  paid_at?: string | null
  /**
   * true όταν το υλικό της (εσωτερικής) έχει ήδη αφαιρεθεί από το απόθεμα, δηλαδή
   * έχει περάσει σε «Σε Παραγωγή». Τότε η ακύρωση/επιστροφή προς τα πίσω ζητά
   * `materials_cut` ("Έχει ήδη κοπεί υλικό;").
   */
  stock_consumed?: boolean
  created_at: string
  updated_at?: string
}

/** Κανονική ή συρόμενη — διαφέρουν στο πορτόφυλλο (η κανονική έχει καβαλίκι). */
/**
 * Τύπος εσωτερικής πόρτας στην αποθήκη.
 *
 * Η ΣΥΡΟΜΕΝΗ ΔΕΝ ΕΙΝΑΙ ΕΔΩ: βγαίνει από την κανονική κόβοντας το καβαλίκι
 * στο πριόνι, οπότε είναι επιλογή της παραγγελίας και όχι ξεχωριστό είδος.
 */
export type InteriorDoorType = "normal" | "no_rebate"

/** Τύπος κάσας εσωτερικής — προκύπτει από την πόρτα, δεν επιλέγεται ελεύθερα. */
export type InteriorKasaType = "standard" | "decorative" | "deep_rebate"

/**
 * Ποια κάσα ταιριάζει σε ποια πόρτα:
 *   κανονική → κανονική πατούρα
 *   κανονική + συρόμενη → διακοσμητική
 *   χωρίς καβαλίκι → μεγάλη πατούρα (κρυφός μεντεσές)
 */
export function kasaTypeForDoor(doorType: InteriorDoorType, sliding: boolean): InteriorKasaType {
  if (doorType === "no_rebate") return "deep_rebate"
  return sliding ? "decorative" : "standard"
}

export type InteriorDoorRecord = {
  id: number
  supplier_id: number
  supplier_name: string
  type: InteriorDoorType
  model: string
  /** Φάρδος σε εκατοστά — το ίδιο μοντέλο έρχεται σε πολλά φάρδη. */
  width: number | null
  color: string
  stock: number
  /** Δεσμευμένα από ανοιχτές παραγγελίες — υπολογίζεται στον server. */
  reserved?: number
}

export type DashboardStatsRecord = {
  today_orders: number
  /** Νέα ενδιάμεση κατάσταση ανάμεσα σε «Σε αναμονή» και «Σε παραγωγή». */
  ready_for_production_orders: number
  in_production: number
  ready_orders: number
  delivered_orders: number
  low_stock_items: number
  /**
   * Παραγγελίες που δεν ξεκίνησαν. Πήρε τη θέση της «αξίας αποθήκης»: δεν
   * υπάρχει καμία στήλη τιμής σε κανένα είδος, οπότε αξία δεν υπολογίζεται.
   */
  pending_orders: number
}

export type TrendPoint = { date: string; orders: number }

export type ActivityRecord = {
  id: number
  type: string
  message: string
  timestamp: string
  user?: string
}

/**
 * Ένα γεγονός της ροής, ωμό.
 *
 * Διαφέρει από το ActivityRecord της αρχικής σελίδας: εκεί έρχεται έτοιμη
 * πρόταση προς ανάγνωση, εδώ τα πεδία ξεχωριστά ώστε το frontend να ξέρει ΤΙ
 * να ξαναφορτώσει και σε ΠΟΙΟΝ ανήκει η ενέργεια.
 */
export type LiveEvent = {
  id: number
  type: string
  /** Έτοιμο κείμενο για γεγονότα που δεν αφορούν μία παραγγελία. */
  summary: string | null
  order_id: number | null
  order_code: string | null
  door_type: string | null
  customer_name: string | null
  from_status: string | null
  to_status: string | null
  actor_id: number | null
  actor_name: string | null
  created_at: string | null
}

export type PaginatedMeta = {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export type Paginated<T> = { data: T[]; meta: PaginatedMeta }

// ── Inventory APIs ───────────────────────────────────────────

export const sasiApi = {
  cacheKey: "erp_sasi",
  cached: () => getCookie<SasiRecord[]>("erp_sasi"),
  cache: (items: SasiRecord[]) => setCookie("erp_sasi", items),
  clearCache: () => deleteCookie("erp_sasi"),
  getAll: () => apiFetch<SasiRecord[]>("/sasi/get/all"),
  getBySku: (sku: string) => apiFetch<SasiRecord>("/sasi/get", "POST", { sku }),
  create: (data: SasiRecord) => apiFetch<{ ok: boolean }>("/sasi/create", "POST", data),
  update: (sku: string, data: Partial<SasiRecord>) =>
    apiFetch<{ ok: boolean }>("/sasi/update", "PUT", { ...data, sku }),
  delete: (sku: string) => apiFetch<{ ok: boolean }>("/sasi/delete", "DELETE", { sku }),
}

export const kasaApi = {
  cacheKey: "erp_kasa",
  cached: () => getCookie<KasaRecord[]>("erp_kasa"),
  cache: (items: KasaRecord[]) => setCookie("erp_kasa", items),
  clearCache: () => deleteCookie("erp_kasa"),
  getAll: () => apiFetch<KasaRecord[]>("/kasa/get/all"),
  getBySku: (sku: string) => apiFetch<KasaRecord>("/kasa/get", "POST", { sku }),
  create: (data: KasaRecord) => apiFetch<{ ok: boolean }>("/kasa/create", "POST", data),
  update: (sku: string, data: Partial<KasaRecord>) =>
    apiFetch<{ ok: boolean }>("/kasa/update", "PUT", { ...data, sku }),
  delete: (sku: string) => apiFetch<{ ok: boolean }>("/kasa/delete", "DELETE", { sku }),
}

export const pervaziApi = {
  cacheKey: "erp_pervazi",
  cached: () => getCookie<PervaziRecord[]>("erp_pervazi"),
  cache: (items: PervaziRecord[]) => setCookie("erp_pervazi", items),
  clearCache: () => deleteCookie("erp_pervazi"),
  getAll: () => apiFetch<PervaziRecord[]>("/pervazi/get/all"),
  getBySku: (sku: string) => apiFetch<PervaziRecord>("/pervazi/get", "POST", { sku }),
  create: (data: PervaziRecord) => apiFetch<{ ok: boolean }>("/pervazi/create", "POST", data),
  update: (sku: string, data: Partial<PervaziRecord>) =>
    apiFetch<{ ok: boolean }>("/pervazi/update", "PUT", { ...data, sku }),
  delete: (sku: string) => apiFetch<{ ok: boolean }>("/pervazi/delete", "DELETE", { sku }),
}

export const ependysiApi = {
  cacheKey: "erp_ependysi",
  cached: () => getCookie<EpendysiRecord[]>("erp_ependysi"),
  cache: (items: EpendysiRecord[]) => setCookie("erp_ependysi", items),
  clearCache: () => deleteCookie("erp_ependysi"),
  getAll: () => apiFetch<EpendysiRecord[]>("/ependysi/get/all"),
  getBySku: (sku: string) => apiFetch<EpendysiRecord>("/ependysi/get", "POST", { sku }),
  create: (data: EpendysiRecord) => apiFetch<{ ok: boolean }>("/ependysi/create", "POST", data),
  update: (sku: string, data: Partial<EpendysiRecord>) =>
    apiFetch<{ ok: boolean }>("/ependysi/update", "PUT", { ...data, sku }),
  delete: (sku: string) => apiFetch<{ ok: boolean }>("/ependysi/delete", "DELETE", { sku }),
}

export type KasaPervaziApi = typeof kasaApi

/**
 * Η ελάχιστη μορφή γραμμής που χειρίζεται ο κοινός πίνακας εξαρτημάτων
 * θωρακισμένων. Το `size` λείπει από το αλουμίνιο και το `side` υπάρχει μόνο
 * στο περβάζι — η κάσα δεν έχει πλευρά, γι' αυτό είναι και τα δύο προαιρετικά.
 */
export type SkuComponentRow = {
  sku: string
  color: string
  stock: number
  /** Δεσμευμένα από ανοιχτές παραγγελίες — υπολογίζεται στον server. */
  reserved?: number
  size?: string
  side?: PervaziSide
  manufacturer_code?: string
}

/** Το συμβόλαιο που χρειάζεται ο πίνακας — γενικό, ώστε κάθε καρτέλα να κρατά τον δικό της τύπο. */
export type SkuComponentApi<T extends SkuComponentRow> = {
  cacheKey: string
  cached: () => T[] | null
  cache: (items: T[]) => void
  clearCache: () => void
  getAll: () => Promise<T[]>
  create: (data: T) => Promise<{ ok: boolean }>
  update: (sku: string, data: Partial<T>) => Promise<{ ok: boolean }>
  delete: (sku: string) => Promise<{ ok: boolean }>
}

/**
 * Παραγγελίες αλουμινίου προς προμηθευτή — ΔΕΝ είναι απόθεμα (δες
 * AluminiumOrder στο backend). Ο κωδικός τον γράφει ο ιδιοκτήτης με το χέρι,
 * γι' αυτό εδώ δεν υπάρχει κανένας auto-generate κωδικού.
 */
export type AluminiumOrderRecord = {
  id: number
  code: string
  /** Ο πραγματικός πελάτης· null στις παλιές εγγραφές με ελεύθερο κείμενο. */
  customer_id: number | null
  /** Η παραγγελία πόρτας για την οποία είναι το αλουμίνιο· προαιρετική. */
  order_id: number | null
  order_code: string | null
  customer_name: string
  description: string
  ordered_at: string
  status: "ordered" | "received"
  received_at: string | null
}

export const aluminiumOrdersApi = {
  getAll: () => apiFetch<AluminiumOrderRecord[]>("/aluminium-orders/get/all"),
  create: (data: {
    code: string
    customer_id?: number
    order_id?: number
    customer_name?: string
    description: string
    ordered_at: string
  }) =>
    apiFetch<{ ok: boolean; order: AluminiumOrderRecord }>("/aluminium-orders/create", "POST", data),
  /** customer_id/order_id: null ξεσυνδέει· η κατάσταση δεν αλλάζει από εδώ. */
  update: (
    id: number,
    data: Partial<
      Pick<AluminiumOrderRecord, "code" | "customer_id" | "order_id" | "customer_name" | "description" | "ordered_at">
    >,
  ) => apiFetch<{ ok: boolean; order: AluminiumOrderRecord }>("/aluminium-orders/update", "PUT", { id, ...data }),
  receive: (id: number) =>
    apiFetch<{ ok: boolean; order: AluminiumOrderRecord }>(`/aluminium-orders/${id}/receive`, "POST"),
}

export const interiorDoorsApi = {
  cacheKey: "erp_interior_doors",
  cached: () => getCookie<InteriorDoorRecord[]>("erp_interior_doors"),
  cache: (items: InteriorDoorRecord[]) => setCookie("erp_interior_doors", items),
  clearCache: () => deleteCookie("erp_interior_doors"),
  getAll: () => apiFetch<InteriorDoorRecord[]>("/inventory/interior-doors/get/all"),
  create: (data: Omit<InteriorDoorRecord, "id">) =>
    apiFetch<{ ok: boolean; item?: InteriorDoorRecord }>("/inventory/interior-doors/create", "POST", data),
  update: (id: number, data: Partial<InteriorDoorRecord>) =>
    apiFetch<{ ok: boolean }>("/inventory/interior-doors/update", "PUT", { id, ...data }),
  delete: (id: number) => apiFetch<{ ok: boolean }>("/inventory/interior-doors/delete", "DELETE", { id }),
}

// ── Εξαρτήματα εσωτερικών πορτών ─────────────────────────────
//
// Κάσες, περβάζια και ξυλόγωνιες: αγοράζονται από προμηθευτές, σε αντίθεση
// με τα εξαρτήματα των θωρακισμένων. Μόνο η κάσα έχει μέγεθος.
//
// Το απόθεμα είναι ΔΕΚΑΔΙΚΟ — μία πόρτα καταναλώνει 2,5 κάσες.

export type InteriorComponentRecord = {
  id: number
  supplier_id: number
  supplier_name: string
  color: string
  stock: number
  /** Δεσμευμένα από ανοιχτές παραγγελίες — υπολογίζεται στον server. */
  reserved?: number
}

export type InteriorKasaRecord = InteriorComponentRecord & {
  size_id: number
  size_name: string
  kasa_type: InteriorKasaType
}

export type InteriorComponentApi<T extends InteriorComponentRecord> = {
  cacheKey: string
  cached: () => T[] | null
  cache: (items: T[]) => void
  clearCache: () => void
  getAll: () => Promise<T[]>
  create: (data: Record<string, unknown>) => Promise<{ ok: boolean; item?: T }>
  update: (id: number, data: Record<string, unknown>) => Promise<{ ok: boolean; item?: T }>
  delete: (id: number) => Promise<{ ok: boolean }>
}

function makeInteriorComponentApi<T extends InteriorComponentRecord>(
  slug: string,
  cookieKey: string,
): InteriorComponentApi<T> {
  const base = `/inventory/${slug}`
  return {
    cacheKey: cookieKey,
    cached: () => getCookie<T[]>(cookieKey),
    cache: (items) => setCookie(cookieKey, items),
    clearCache: () => deleteCookie(cookieKey),
    getAll: () => apiFetch<T[]>(`${base}/get/all`),
    create: (data) => apiFetch<{ ok: boolean; item?: T }>(`${base}/create`, "POST", data),
    // Το id μπαίνει ΤΕΛΕΥΤΑΙΟ ώστε να μην μπορεί ένα πεδίο του data να
    // σκεπάσει το αναγνωριστικό με το οποίο βρίσκεται η εγγραφή.
    update: (id, data) => apiFetch<{ ok: boolean; item?: T }>(`${base}/update`, "PUT", { ...data, id }),
    delete: (id) => apiFetch<{ ok: boolean }>(`${base}/delete`, "DELETE", { id }),
  }
}

export const interiorKasaApi = makeInteriorComponentApi<InteriorKasaRecord>("interior-kasa", "erp_interior_kasa")
export const interiorPervaziApi = makeInteriorComponentApi<InteriorComponentRecord>(
  "interior-pervazi",
  "erp_interior_pervazi",
)
export const interiorKsilogoniaApi = makeInteriorComponentApi<InteriorComponentRecord>(
  "interior-ksilogonia",
  "erp_interior_ksilogonia",
)
export const interiorBiniaApi = makeInteriorComponentApi<InteriorComponentRecord>(
  "interior-binia",
  "erp_interior_binia",
)

// ── Customers API ────────────────────────────────────────────

// Χωρίς cache helpers σκόπιμα: οι πελάτες είναι προσωπικά δεδομένα (ονόματα,
// τηλέφωνα). Δεν καλούνταν από πουθενά — μην τα επαναφέρεις χωρίς λόγο.
export const customersApi = {
  getAll: (
    params?: { search?: string; page?: number; per_page?: number; incomplete?: boolean },
    signal?: AbortSignal,
  ) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set("search", params.search)
    if (params?.page) qs.set("page", String(params.page))
    if (params?.per_page) qs.set("per_page", String(params.per_page))
    if (params?.incomplete) qs.set("incomplete", "1")
    const q = qs.toString()
    return apiFetch<Paginated<CustomerRecord>>(
      `/customers/get/all${q ? `?${q}` : ""}`,
      "GET",
      undefined,
      signal,
    )
  },
  getById: (id: number) => apiFetch<CustomerRecord>(`/customers/get/${id}`),
  create: (data: CustomerPayload) =>
    apiFetch<{ ok: boolean; customer: CustomerRecord }>("/customers/create", "POST", data),
  update: (id: number, data: CustomerPayload) =>
    apiFetch<{ ok: boolean; customer: CustomerRecord }>("/customers/update", "PUT", { id, ...data }),
  delete: (id: number) => apiFetch<{ ok: boolean }>("/customers/delete", "DELETE", { id }),
}

// ── Orders API ───────────────────────────────────────────────

/** Ένα υλικό που δεν επαρκεί για την παραγγελία που γράφεται. */
export type StockShortage = {
  material: string
  /** Τεχνικό κλειδί, π.χ. "internal|Λευκό" — το UI το μεταφράζει. */
  label: string
  /**
   * Έτοιμο κείμενο από τον server, όπου το κλειδί δεν διαβάζεται από άνθρωπο.
   * Σήμερα μόνο η εσωτερική πόρτα, που κλειδώνεται με το id της αποθήκης.
   */
  title?: string | null
  needed: number
  available: number
}

/** Αποτέλεσμα σκαναρίσματος QR — τι έκανε ο server με την παραγγελία. */
export type ScanResult = "changed" | "already_ready" | "blocked"

export type ScanResponse = {
  result: ScanResult
  /** Ο λόγος άρνησης, όταν ο διακομιστής τον δίνει (π.χ. «Εκκρεμεί»). */
  message?: string
  order: {
    id: number
    order_number: string
    customer_name: string
    status: OrderRecord["status"]
  }
}

// Ομοίως χωρίς cache: οι παραγγελίες περιέχουν διευθύνσεις παράδοσης.
export const ordersApi = {
  getAll: (params?: {
    search?: string
    status?: string
    page?: number
    per_page?: number
    type?: string
    customer_id?: number
    /**
     * Ποιες καταστάσεις. Ο ΔΙΑΧΩΡΙΣΜΟΣ ΓΙΝΕΤΑΙ ΣΤΟΝ SERVER — μέχρι πρότινος
     * το φιλτράρισμα γινόταν εδώ, ΜΕΤΑ τη σελιδοποίηση, οπότε η σελίδα
     * έδειχνε λιγότερες γραμμές από όσες υποσχόταν και το σύνολο μετρούσε
     * παραγγελίες που δεν εμφανίζονταν ποτέ.
     *
     * Παραλείποντάς το: «active». Με `customer_id`: «all», γιατί μια καρτέλα
     * πελάτη τα θέλει όλα.
     */
    scope?: "active" | "history" | "all"
    from?: string
    to?: string
    /** Ποιος την έφτιαξε. Παραλείποντάς το: όλοι. */
    created_by?: number
    /** Εξοφλημένη ή όχι. Παραλείποντάς το: και οι δύο. */
    paid?: "yes" | "no"
  }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set("search", params.search)
    if (params?.status) qs.set("status", params.status)
    if (params?.scope) qs.set("scope", params.scope)
    if (params?.page) qs.set("page", String(params.page))
    if (params?.per_page) qs.set("per_page", String(params.per_page))
    if (params?.type) qs.set("type", params.type)
    if (params?.customer_id) qs.set("customer_id", String(params.customer_id))
    if (params?.from) qs.set("from", params.from)
    if (params?.to) qs.set("to", params.to)
    if (params?.created_by) qs.set("created_by", String(params.created_by))
    if (params?.paid) qs.set("paid", params.paid)
    const q = qs.toString()
    return apiFetch<Paginated<OrderRecord>>(`/orders/get/all${q ? `?${q}` : ""}`)
  },
  /**
   * Ποιοι εμφανίζονται όντως ως δημιουργοί παραγγελιών — για το μενού
   * φίλτρου. ΟΧΙ η λίστα προσωπικού (staffApi): εκείνη ζει πίσω από το
   * δικαίωμα `staff`, που ο πωλητής δεν έχει.
   */
  creators: () => apiFetch<{ creators: { id: number; name: string }[] }>("/orders/creators"),
  /** Μία παραγγελία ΜΕ τις πόρτες της — για PDF και επεξεργασία. */
  getById: (id: number) => apiFetch<OrderRecord>(`/orders/get/${id}`),
  /**
   * Μία παραγγελία για την οθόνη παραγωγής (/production/:id) — ΧΩΡΙΣ
   * οικονομικά. Δικό της endpoint αντί για getById(): εκείνο χτυπάει
   * /orders/get/{id}, που απαιτεί `orders` — δικαίωμα που ο εργάτης
   * παραγωγής (μόνο `production`) δεν έχει ποτέ.
   */
  getForProduction: (id: number) =>
    apiFetch<{ order: ProductionOrderRecord }>(`/orders/${id}/production-view`),
  create: (data: Record<string, unknown>) =>
    apiFetch<{ ok: boolean; order: OrderRecord }>("/orders/create", "POST", data),
  update: (id: number, data: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>("/orders/update", "PUT", { id, ...data }),
  delete: (id: number) => apiFetch<{ ok: boolean }>("/orders/delete", "DELETE", { id }),
  /**
   * Τι λείπει από την αποθήκη για μια παραγγελία που δεν έχει σωθεί ακόμα.
   *
   * Ο υπολογισμός γίνεται στον server ώστε να χρησιμοποιεί τους ίδιους κανόνες
   * με τη δέσμευση. Το `exclude_order_id` είναι απαραίτητο στην επεξεργασία:
   * οι πόρτες της φόρμας αντικαθιστούν τις αποθηκευμένες.
   */
  checkStock: (body: {
    door_type: "security" | "interior"
    doors: Record<string, unknown>[]
    exclude_order_id?: number
  }) => apiFetch<{ shortages: StockShortage[] }>("/orders/check-stock", "POST", body),

  /** Σκανάρισμα QR: κάνει την παραγγελία έτοιμη. POST, ποτέ GET. */
  markReady: (id: number) => apiFetch<ScanResponse>(`/orders/${id}/mark-ready`, "POST"),
  /**
   * Αλλαγή κατάστασης ΑΠΟ ΤΟΝ ΠΙΝΑΚΑ ΠΑΡΑΓΩΓΗΣ. Ξεχωριστό από το `update()`:
   * αυτό εδώ απαιτεί μόνο `production`, όχι `orders:full` — έτσι ο εργάτης
   * στο tablet κινεί κάρτες χωρίς να αποκτά δικαίωμα επεξεργασίας.
   */
  updateProductionStatus: (
    id: number,
    status: "ready_for_production" | "in_production" | "ready",
    /** Μόνο όταν το υλικό έχει ήδη αφαιρεθεί και η κάρτα γυρίζει πίσω. */
    materialsCut?: boolean,
  ) =>
    apiFetch<{ ok: boolean }>(`/orders/${id}/production-status`, "PUT", {
      status,
      ...(materialsCut === undefined ? {} : { materials_cut: materialsCut }),
    }),
  /**
   * Σημειώνει εξόφληση. Ιδεμποτικό στον server — δεύτερο πάτημα δεν αλλάζει
   * την ημερομηνία που ήδη καταγράφηκε.
   */
  markPaid: (id: number) => apiFetch<{ ok: boolean; order: OrderRecord }>(`/orders/${id}/mark-paid`, "POST"),
  /**
   * Αναίρεση σήμανσης εξόφλησης. ΜΟΝΟ Admin στον server (perm:purge) — μια
   * λάθος σήμανση δεν πρέπει να μένει για πάντα χωρίς επιστροφή.
   */
  unmarkPaid: (id: number) => apiFetch<{ ok: boolean; order: OrderRecord }>(`/orders/${id}/unmark-paid`, "POST"),
  /**
   * Σημειώνει ΜΙΑ πόρτα παραδομένη. Ιδεμποτικό στον server — δεύτερο πάτημα
   * δεν αλλάζει την ημερομηνία που ήδη καταγράφηκε. Εμφανίζεται μόνο σε
   * αποθηκευμένη πόρτα (έχει id) — δες OrderDetail.tsx.
   */
  deliverDoor: (orderId: number, doorId: number) =>
    apiFetch<{ ok: boolean; door: OrderDoorRecord }>(`/orders/${orderId}/doors/${doorId}/deliver`, "POST"),
  /**
   * Αναίρεση παράδοσης πόρτας. ΜΟΝΟ Admin στον server (perm:purge) — ίδιο
   * σκεπτικό με το unmarkPaid.
   */
  undeliverDoor: (orderId: number, doorId: number) =>
    apiFetch<{ ok: boolean; door: OrderDoorRecord }>(`/orders/${orderId}/doors/${doorId}/undeliver`, "POST"),
}

/** @deprecated Ψεύτικος διαχωρισμός — είναι το ίδιο αντικείμενο. Χρησιμοποίησε `ordersApi`. */
export const interiorDoorOrdersApi = ordersApi

// ── Dashboard API ────────────────────────────────────────────

export const dashboardApiService = {
  getStats: () => apiFetch<DashboardStatsRecord>("/dashboard/stats"),
  getRecentOrders: (limit = 10) => apiFetch<OrderRecord[]>(`/dashboard/recent-orders?limit=${limit}`),
  getActivity: (limit = 20) => apiFetch<ActivityRecord[]>(`/dashboard/activity?limit=${limit}`),
  getOrdersTrend: (days = 14) => apiFetch<TrendPoint[]>(`/dashboard/orders-trend?days=${days}`),
}

// ── Ροή γεγονότων (live updates) ──────────────────────────────

export const activityApi = {
  /**
   * `after` κενό = δώσε μου τα πιο πρόσφατα (αρχικό γέμισμα).
   * `after` με id = δώσε μου ΜΟΝΟ ό,τι έγινε μετά (ροή).
   */
  since: (after?: number | null, limit = 30) => {
    const qs = new URLSearchParams()
    if (after !== null && after !== undefined) qs.set("after", String(after))
    qs.set("limit", String(limit))
    return apiFetch<{ events: LiveEvent[]; last_id: number }>(`/activity?${qs.toString()}`)
  },
}

// ── Προσωπικό — ΠΡΑΓΜΑΤΙΚΟΙ λογαριασμοί ───────────────────────

export type StaffRecord = {
  id: number
  name: string
  email: string
  role: string
  permissions: PermissionMap
  /** Ο server το λέει, ώστε το UI να μην προσφέρει κουμπιά που θα απορριφθούν. */
  is_self: boolean
}

/**
 * Ο κατάλογος ενοτήτων και τα πακέτα ρόλων ΕΡΧΟΝΤΑΙ ΑΠΟ ΤΟΝ SERVER — δεν
 * αντιγράφονται εδώ. Δύο αντίγραφα αποκλίνουν την πρώτη φορά που θα αλλάξει
 * ένα πακέτο, και τότε η οθόνη προσυμπληρώνει άλλα από όσα επιβάλλει ο server.
 */
export type StaffMeta = {
  resources: { key: string; levels: PermissionLevel[] }[]
  roles: { key: string; permissions: PermissionMap }[]
}

export const staffApi = {
  getAll: () => apiFetch<{ users: StaffRecord[] }>("/staff/get/all"),
  meta: () => apiFetch<StaffMeta>("/staff/meta"),
  create: (body: { name: string; email: string; password: string; role: string; permissions: PermissionMap }) =>
    apiFetch<{ ok: boolean; id: number }>("/staff/create", "POST", body),
  update: (body: { id: number; name?: string; email?: string; role?: string; permissions?: PermissionMap }) =>
    apiFetch<{ ok: boolean }>("/staff/update", "PUT", body),
  resetPassword: (id: number, password: string) =>
    apiFetch<{ ok: boolean }>("/staff/reset-password", "PUT", { id, password }),
  delete: (id: number) => apiFetch<{ ok: boolean }>("/staff/delete", "DELETE", { id }),
}

// ── Ελλείψεις υλικών ──────────────────────────────────────────

export type ShortageOrder = { id: number; code: string; customer_name: string; needed: number; covered: boolean }

export type ShortageRow = {
  material: string
  /**
   * Ποιο είδος πόρτας το ζητά — «security» ή «interior». Το δίνει ο server
   * (StockReservation::shortageReport), ίδια πηγή με τον υπολογισμό των
   * ελλείψεων: έτσι το χαρτί εκτύπωσης δεν ξαναμαντεύει από το όνομα του
   * υλικού.
   */
  door_type: "security" | "interior"
  label: string
  /** Δες StockShortage.title — «114» δεν λέει τίποτα σε κανέναν. */
  title?: string | null
  needed: number
  stock: number
  missing: number
  /** false = δεν υπάρχει εγγραφή αποθήκης, άρα δεν γίνεται παραλαβή. */
  has_row: boolean
  /** Μόνο οι εσωτερικές γραμμές έχουν προμηθευτή· οι θωρακισμένες πάντα null. */
  supplier_id: number | null
  supplier_name?: string | null
  orders: ShortageOrder[]
}

export const shortagesApi = {
  getAll: () => apiFetch<{ shortages: ShortageRow[] }>("/inventory/shortages"),
  receive: (items: { material: string; label: string; quantity: number; order_ids?: number[] }[]) =>
    apiFetch<{ ok: boolean; unblocked: number }>("/inventory/receive", "POST", { items }),
  registerMaterial: (material: string, label: string) =>
    apiFetch<{ ok: boolean; created: boolean }>("/inventory/register-material", "POST", { material, label }),
}

// ── Settings API factory ─────────────────────────────────────

export type SettingsApiModule = {
  cacheKey: string
  cached: () => SettingRecord[] | null
  cache: (items: SettingRecord[]) => void
  clearCache: () => void
  getAll: () => Promise<SettingRecord[]>
  create: (name: string, quantity?: number | null, supplierId?: number | null) => Promise<{ ok: boolean }>
  update: (
    id: string,
    name: string,
    quantity?: number | null,
    supplierId?: number | null,
  ) => Promise<{ ok: boolean }>
  toggle: (id: string, status: "active" | "inactive") => Promise<{ ok: boolean }>
  delete: (id: string) => Promise<{ ok: boolean }>
}

export function makeSettingsApi(slug: string, cookieKey: string): SettingsApiModule {
  return {
    cacheKey: cookieKey,
    cached: () => getCookie<SettingRecord[]>(cookieKey),
    cache: (items) => setCookie(cookieKey, items),
    clearCache: () => deleteCookie(cookieKey),
    getAll: () => apiFetch<SettingRecord[]>(`/settings/${slug}/get/all`),
    create: (name, quantity, supplierId) =>
      apiFetch<{ ok: boolean }>(`/settings/${slug}/create`, "POST", {
        name,
        ...(quantity != null ? { quantity } : {}),
        ...(supplierId != null ? { supplier_id: supplierId } : {}),
      }),
    update: (id, name, quantity, supplierId) =>
      apiFetch<{ ok: boolean }>(`/settings/${slug}/update`, "PUT", {
        id,
        name,
        ...(quantity != null ? { quantity } : {}),
        ...(supplierId != null ? { supplier_id: supplierId } : {}),
      }),
    toggle: (id, status) => apiFetch<{ ok: boolean }>(`/settings/${slug}/toggle`, "PUT", { id, status }),
    delete: (id) => apiFetch<{ ok: boolean }>(`/settings/${slug}/delete`, "DELETE", { id }),
  }
}

export const suppliersApi = makeSettingsApi("suppliers", "erp_suppliers")
// Ένα χρωματολόγιο για ΟΛΑ τα θωρακισμένα — σασί, κάσες, επενδύσεις, αλουμίνια.
// Η παλιά ξεχωριστή λίστα «Χρώματα Κάσας» καταργήθηκε: τα χρώματα είναι ίδια.
export const securityDoorColorsApi = makeSettingsApi("security-door-colors", "erp_sd_colors")
export const interiorColorsApi = makeSettingsApi("interior-colors", "erp_interior_colors")
/**
 * Ένας κατάλογος σχεδίων για τις δύο όψεις.
 *
 * Το σχέδιο χαράσσεται στην επένδυση, οπότε είναι το ίδιο μέσα και έξω. Η
 * παραγγελία κρατά ξεχωριστή επιλογή ανά πλευρά — μόνο ο κατάλογος είναι κοινός.
 */
export const designsApi = makeSettingsApi("designs", "erp_designs")
export const accessoriesApi = makeSettingsApi("accessories", "erp_accessories")
export const frameSizesApi = makeSettingsApi("frame-sizes", "erp_frame_sizes")
export const kasaSizesApi = makeSettingsApi("kasa-sizes", "erp_kasa_sizes")
